import { deriveDirectionAndRole, deriveRemoteUserId, makeActiveCallContext, makeInitialCallModel, makeUnknownActiveCallContext, } from "./callModel.js";
import { CALL_SLOT_STATE, } from "./callStates.js";
import { SIGNALING_CONNECTION_STATE, RESUME_STATE, } from "./signalingTypes.js";
import { MEDIA_CAPTURE_STATE } from "./mediaTypes.js";
import { CONTROLLER_EVENT_TYPE as CET } from "./controllerEvents.js";
import { CALL_STATE as SCS } from "../server-contracts/callStates.js";
import { CALL_DIRECTION, CALL_ROLE } from "./callDirections.js";
/**
 * Reducer invariants
 * ------------------
 *
 * - callSlotState === ACTIVE => activeCall !== null
 * - callSlotState !== ACTIVE => activeCall === null
 * - server lifecycle is authoritative when provided by server events
 * - signaling/media/rtc subtrees evolve independently except where explicitly coordinated
 * - reducer remains pure: no Date.now() or other ambient time reads
 */
function withActiveCall(model, activeCall) {
    return {
        ...model,
        callSlotState: CALL_SLOT_STATE.ACTIVE,
        activeCall,
    };
}
function clearActiveCall(model) {
    return {
        ...model,
        callSlotState: CALL_SLOT_STATE.IDLE,
        activeCall: null,
    };
}
function withResetRtcState(model) {
    return {
        ...model,
        rtc: makeInitialCallModel().rtc,
    };
}
function ensureActiveCall(model) {
    return model.callSlotState === CALL_SLOT_STATE.ACTIVE ? model.activeCall : null;
}
function computeRingingExpiresAt(args) {
    return args.nowTs + args.expiresInSec * 1000;
}
function applyServerCallStateSnapshot(model, snapshot) {
    const localUserId = model.session?.userId ?? null;
    const { direction, selfRole } = deriveDirectionAndRole({
        localUserId,
        callerUserId: snapshot.callerUserId,
        calleeUserId: snapshot.calleeUserId,
    });
    const remoteUserId = deriveRemoteUserId({
        localUserId,
        callerUserId: snapshot.callerUserId,
        calleeUserId: snapshot.calleeUserId,
    });
    const activeCall = makeActiveCallContext({
        callId: snapshot.callId,
        serverCallState: snapshot.state,
        direction,
        selfRole,
        callerUserId: snapshot.callerUserId,
        calleeUserId: snapshot.calleeUserId,
        remoteUserId,
        media: snapshot.media,
        features: snapshot.features,
        participants: snapshot.participants,
        updatedAt: snapshot.updatedAt,
        ringingExpiresAt: null,
        endedReason: null,
        endedBy: null,
    });
    return withActiveCall(model, activeCall);
}
export function reduceCallModel(model, event) {
    switch (event.type) {
        case CET.SIGNALING_CONNECT_STARTED:
            return {
                ...model,
                signalingConnectionState: SIGNALING_CONNECTION_STATE.CONNECTING,
                error: null,
            };
        case CET.SIGNALING_TRANSPORT_OPENED:
            return {
                ...model,
                signalingConnectionState: SIGNALING_CONNECTION_STATE.CONNECTED,
            };
        case CET.SIGNALING_TRANSPORT_CLOSED: {
            const hasActiveCall = model.callSlotState === CALL_SLOT_STATE.ACTIVE;
            return {
                ...model,
                signalingConnectionState: hasActiveCall
                    ? SIGNALING_CONNECTION_STATE.RECONNECTING
                    : SIGNALING_CONNECTION_STATE.DISCONNECTED,
            };
        }
        case CET.SIGNALING_RESUME_STARTED:
            return {
                ...model,
                resumeState: RESUME_STATE.ATTEMPTING,
                signalingConnectionState: SIGNALING_CONNECTION_STATE.RECONNECTING,
            };
        case CET.WELCOME_RECEIVED: {
            const resumeState = event.payload.resume.ok === true
                ? RESUME_STATE.SUCCESS
                : model.resumeState === RESUME_STATE.ATTEMPTING
                    ? RESUME_STATE.FAILED
                    : RESUME_STATE.NONE;
            return {
                ...model,
                signalingConnectionState: SIGNALING_CONNECTION_STATE.AUTHENTICATED,
                resumeState,
                session: {
                    sessionId: event.payload.sessionId,
                    userId: event.payload.userId,
                    heartbeatSec: event.payload.heartbeatSec,
                },
                error: null,
            };
        }
        case CET.ERROR_RECEIVED:
            return {
                ...model,
                error: {
                    code: event.payload.code,
                    message: event.payload.message,
                    fatal: event.payload.fatal,
                },
                signalingConnectionState: event.payload.fatal
                    ? SIGNALING_CONNECTION_STATE.ERROR
                    : model.signalingConnectionState,
                callSlotState: model.callSlotState === CALL_SLOT_STATE.ACTIVE
                    ? model.callSlotState
                    : event.payload.fatal
                        ? CALL_SLOT_STATE.ERROR
                        : model.callSlotState,
            };
        case CET.PONG_RECEIVED:
            return model;
        case CET.CALL_CREATED_RECEIVED: {
            const localUserId = model.session?.userId ?? null;
            const callerUserId = localUserId ?? "";
            const calleeUserId = event.payload.calleeUserId;
            const activeCall = makeUnknownActiveCallContext({
                callId: event.callId,
                serverCallState: event.payload.state,
                direction: localUserId ? CALL_DIRECTION.OUTGOING : CALL_DIRECTION.NONE,
                selfRole: localUserId ? CALL_ROLE.CALLER : CALL_ROLE.NONE,
                callerUserId,
                calleeUserId,
                remoteUserId: localUserId ? calleeUserId : null,
                media: { audio: true, video: true },
                updatedAt: event.observedAtTs,
                ringingExpiresAt: null,
            });
            return withActiveCall(model, activeCall);
        }
        case CET.CALL_INCOMING_RECEIVED: {
            const localUserId = model.session?.userId ?? null;
            const callerUserId = event.payload.fromUserId;
            const calleeUserId = localUserId ?? "";
            const activeCall = makeUnknownActiveCallContext({
                callId: event.callId,
                serverCallState: SCS.RINGING,
                direction: localUserId ? CALL_DIRECTION.INCOMING : CALL_DIRECTION.NONE,
                selfRole: localUserId ? CALL_ROLE.CALLEE : CALL_ROLE.NONE,
                callerUserId,
                calleeUserId,
                remoteUserId: event.payload.fromUserId,
                media: event.payload.media,
                updatedAt: event.observedAtTs,
                ringingExpiresAt: computeRingingExpiresAt({
                    nowTs: event.observedAtTs,
                    expiresInSec: event.payload.expiresInSec,
                }),
            });
            return withActiveCall(model, activeCall);
        }
        case CET.CALL_ACCEPTED_RECEIVED: {
            const activeCall = ensureActiveCall(model);
            if (!activeCall || activeCall.callId !== event.callId)
                return model;
            return withActiveCall(model, {
                ...activeCall,
                serverCallState: SCS.NEGOTIATING,
                updatedAt: event.observedAtTs,
            });
        }
        case CET.CALL_RECONNECTING_RECEIVED: {
            const activeCall = ensureActiveCall(model);
            if (!activeCall || activeCall.callId !== event.callId)
                return model;
            return {
                ...withActiveCall(model, {
                    ...activeCall,
                    serverCallState: SCS.RECONNECTING,
                    updatedAt: event.observedAtTs,
                }),
                signalingConnectionState: SIGNALING_CONNECTION_STATE.RECONNECTING,
            };
        }
        case CET.CALL_IN_CALL_RECEIVED: {
            const activeCall = ensureActiveCall(model);
            if (!activeCall || activeCall.callId !== event.callId)
                return model;
            return {
                ...withActiveCall(model, {
                    ...activeCall,
                    serverCallState: SCS.IN_CALL,
                    ringingExpiresAt: null,
                    updatedAt: event.observedAtTs,
                }),
                signalingConnectionState: SIGNALING_CONNECTION_STATE.READY,
            };
        }
        case CET.CALL_RESUMED_RECEIVED: {
            const activeCall = ensureActiveCall(model);
            if (!activeCall || activeCall.callId !== event.callId)
                return model;
            return {
                ...withActiveCall(model, {
                    ...activeCall,
                    serverCallState: SCS.IN_CALL,
                    updatedAt: event.observedAtTs,
                }),
                signalingConnectionState: SIGNALING_CONNECTION_STATE.READY,
            };
        }
        case CET.CALL_ENDED_RECEIVED: {
            const activeCall = ensureActiveCall(model);
            if (!activeCall || activeCall.callId !== event.callId)
                return model;
            const next = withResetRtcState(clearActiveCall(model));
            return {
                ...next,
                signalingConnectionState: model.signalingConnectionState === SIGNALING_CONNECTION_STATE.ERROR
                    ? model.signalingConnectionState
                    : SIGNALING_CONNECTION_STATE.READY,
            };
        }
        case CET.SDP_OFFER_RECEIVED:
        case CET.SDP_ANSWER_RECEIVED:
        case CET.ICE_CANDIDATE_RECEIVED:
            return model;
        case CET.CALL_STATE_RECEIVED: {
            const next = applyServerCallStateSnapshot(model, event.payload);
            return {
                ...next,
                signalingConnectionState: SIGNALING_CONNECTION_STATE.READY,
            };
        }
        case CET.MEDIA_ACQUIRE_STARTED:
            return {
                ...model,
                media: {
                    ...model.media,
                    captureState: MEDIA_CAPTURE_STATE.ACQUIRING,
                    requestedAudio: event.requestedAudio,
                    requestedVideo: event.requestedVideo,
                    error: null,
                },
            };
        case CET.MEDIA_ACQUIRE_SUCCEEDED:
            return {
                ...model,
                media: {
                    ...model.media,
                    captureState: MEDIA_CAPTURE_STATE.READY,
                    hasLocalStream: event.hasAudioTrack || event.hasVideoTrack,
                    audio: {
                        available: event.hasAudioTrack,
                        enabled: event.hasAudioTrack,
                    },
                    video: {
                        available: event.hasVideoTrack,
                        enabled: event.hasVideoTrack,
                    },
                    error: null,
                },
            };
        case CET.MEDIA_ACQUIRE_FAILED:
            return {
                ...model,
                media: {
                    ...model.media,
                    captureState: MEDIA_CAPTURE_STATE.ERROR,
                    hasLocalStream: false,
                    audio: {
                        ...model.media.audio,
                        available: false,
                        enabled: false,
                    },
                    video: {
                        ...model.media.video,
                        available: false,
                        enabled: false,
                    },
                    error: {
                        code: event.payload.code,
                        message: event.payload.message,
                    },
                },
            };
        case CET.MEDIA_DEVICES_UPDATED:
            return {
                ...model,
                media: {
                    ...model.media,
                    devices: event.devices,
                },
            };
        case CET.MEDIA_SELECTED_DEVICES_UPDATED:
            return {
                ...model,
                media: {
                    ...model.media,
                    selectedDevices: event.selectedDevices,
                },
            };
        case CET.LOCAL_AUDIO_ENABLED_CHANGED:
            return {
                ...model,
                media: {
                    ...model.media,
                    audio: {
                        ...model.media.audio,
                        enabled: event.enabled,
                    },
                },
            };
        case CET.LOCAL_VIDEO_ENABLED_CHANGED:
            return {
                ...model,
                media: {
                    ...model.media,
                    video: {
                        ...model.media.video,
                        enabled: event.enabled,
                    },
                },
            };
        case CET.LOCAL_SPEAKING_DETECTED:
            return {
                ...model,
                media: {
                    ...model.media,
                    isSpeaking: event.isSpeaking,
                },
            };
        case CET.RTC_CONNECTION_STATE_CHANGED:
            return {
                ...model,
                rtc: {
                    ...model.rtc,
                    connectionState: event.connectionState,
                },
            };
        case CET.RTC_ICE_GATHERING_STATE_CHANGED:
            return {
                ...model,
                rtc: {
                    ...model.rtc,
                    iceGatheringState: event.iceGatheringState,
                },
            };
        case CET.RTC_LOCAL_DESCRIPTION_SET:
            return {
                ...model,
                rtc: {
                    ...model.rtc,
                    descriptions: {
                        ...model.rtc.descriptions,
                        hasLocalDescription: true,
                    },
                },
            };
        case CET.RTC_REMOTE_DESCRIPTION_SET:
            return {
                ...model,
                rtc: {
                    ...model.rtc,
                    descriptions: {
                        ...model.rtc.descriptions,
                        hasRemoteDescription: true,
                    },
                },
            };
        case CET.RTC_REMOTE_MEDIA_UPDATED:
            return {
                ...model,
                rtc: {
                    ...model.rtc,
                    remoteMedia: {
                        hasRemoteStream: event.hasRemoteStream,
                        hasRemoteAudioTrack: event.hasRemoteAudioTrack,
                        hasRemoteVideoTrack: event.hasRemoteVideoTrack,
                    },
                },
            };
        case CET.RTC_NEGOTIATION_NEEDED_CHANGED:
            return {
                ...model,
                rtc: {
                    ...model.rtc,
                    isNegotiationNeeded: event.isNegotiationNeeded,
                },
            };
        case CET.RTC_MAKING_OFFER_CHANGED:
            return {
                ...model,
                rtc: {
                    ...model.rtc,
                    isMakingOffer: event.isMakingOffer,
                },
            };
        case CET.RTC_SETTING_REMOTE_ANSWER_PENDING_CHANGED:
            return {
                ...model,
                rtc: {
                    ...model.rtc,
                    isSettingRemoteAnswerPending: event.isSettingRemoteAnswerPending,
                },
            };
        case CET.RTC_ERROR_OCCURRED:
            return {
                ...model,
                rtc: {
                    ...model.rtc,
                    error: {
                        code: event.payload.code,
                        message: event.payload.message,
                    },
                },
            };
        default: {
            const _exhaustive = event;
            return _exhaustive;
        }
    }
}
